import React, { useState, useEffect } from 'react';
import { Auth } from '@supabase/auth-ui-react';
import { ThemeSupa } from '@supabase/auth-ui-shared';
import { supabase } from '@/supabaseClient';
import { trackSignIn } from '@/services/analytics/index.js';

export const AuthModal = ({ isOpen, onClose, onSuccess }) => {
  const [inAppBrowser, setInAppBrowser] = useState(false);
  const [availableProviders, setAvailableProviders] = useState(['google', 'apple', 'facebook']);

  // Check if running in an in-app browser on component mount
  useEffect(() => {
    const isInApp = /Instagram|FBAN|FBAV|Twitter|Line|WhatsApp|Telegram/i.test(navigator.userAgent);
    setInAppBrowser(isInApp);
    
    if (isInApp) {
      console.log('In-app browser detected, limiting authentication providers');
      // In in-app browsers, we might want to limit providers or use different ones
      setAvailableProviders(['google']); // More reliable in in-app browsers
    }
  }, []);

  // Listen for auth state changes
  useEffect(() => {
    if (!isOpen) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' && session) {
        console.log('User signed in successfully');
        
        // Track the sign-in event
        const provider = session.user?.app_metadata?.provider || 'email';
        trackSignIn(provider);
        
        onSuccess?.();
        onClose();
      }
    });

    return () => subscription.unsubscribe();
  }, [isOpen, onSuccess, onClose]);

  // Detect if user is on mobile device
  const isMobile = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Prevent auto-focus on mobile devices
  useEffect(() => {
    if (isMobile && isOpen) {
      const timer = setTimeout(() => {
        // Find and blur any focused input in the auth component
        const activeElement = document.activeElement;
        if (activeElement && activeElement.tagName === 'INPUT') {
          activeElement.blur();
        }
        
        // Also prevent future auto-focus by adding event listeners
        const inputs = document.querySelectorAll('.supabase-auth-ui_ui input');
        inputs.forEach(input => {
          // Remove autofocus attribute if it exists
          input.removeAttribute('autofocus');
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isMobile, isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-md transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute right-4 top-4 z-10 text-gray-400 hover:text-gray-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Content */}
          <div className="p-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Welcome to Zaptopia
              </h2>
              <p className="text-gray-600">
                Sign in to vote, comment, and submit app ideas
              </p>
            </div>

            {/* Show warning message for in-app browsers */}
            {inAppBrowser && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-600" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Browser Compatibility Notice
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>For the best experience, please open this page in your default browser.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <Auth
              supabaseClient={supabase}
              appearance={{
                theme: ThemeSupa,
                variables: {
                  default: {
                    colors: {
                      brand: '#4f46e5',
                      brandAccent: '#4338ca',
                      brandButtonText: 'white',
                      inputLabelText: 'gray',
                      inputText: 'black',
                      inputBorder: 'lightgray',
                      inputBackground: 'white',
                      messageText: 'gray',
                    },
                    borderWidths: {
                      buttonBorderWidth: '1px',
                      inputBorderWidth: '1px',
                    },
                    radii: {
                      borderRadiusButton: '8px',
                      buttonBorderRadius: '8px',
                      inputBorderRadius: '8px',
                    },
                  },
                },
                style: {
                  button: {
                    fontSize: '16px',
                    padding: '12px 16px',
                    transition: 'all 0.2s ease',
                    fontWeight: '500',
                  },
                  input: {
                    fontSize: '16px',
                    padding: '12px 16px',
                  },
                  label: {
                    fontSize: '14px',
                    marginBottom: '6px',
                    fontWeight: '500',
                  },
                  anchor: {
                    color: '#4f46e5',
                    textDecoration: 'none',
                  },
                  message: {
                    padding: '12px',
                    fontSize: '14px',
                    borderRadius: '8px',
                  },
                },
              }}
              providers={availableProviders}
              magicLink={true}
              view="magic_link"
              theme="light"
            />

            <p className="mt-4 text-sm text-gray-500 text-center">
              By signing in, you agree to our{' '}
              <a href="/terms-of-service" className="text-indigo-600 hover:text-indigo-500" target="_blank">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacy-policy" className="text-indigo-600 hover:text-indigo-500" target="_blank">
                Privacy Policy
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};